import React, { Component } from 'react'
import { isEqual } from 'lodash'

import './dropdown.scss'

// Components
import LabelWrapper from '../labelWrapper'
import Activator from './Activator'
import SelectBox from './SelectBox'

/**
 * Implements a component to show a drop down.
 * - When use of prop "options", send an array containing { label: 0, value: 0 }.
 *  - label is the displayed text value
 *  - value is the returned value when selected
 */
class DropDown extends Component {

  constructor (props) {
    super(props)
    this.state = {
      isOpen: false,
      value: null,
      options: [],
      selectedOptions: [],

      activatorStateClass: '',
      openOnTop: false
    }

    this.setRef = this.setRef.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.onChange = this.onChange.bind(this)
    this.toggleOpen = this.toggleOpen.bind(this)
    this.isSelected = this.isSelected.bind(this)
    this.findSelectedOptions = this.findSelectedOptions.bind(this)
    this.getStateStyle = this.getStateStyle.bind(this)
  }

  componentDidMount () {
    document.addEventListener('mouseup', this.handleClickOutside, this)
    document.addEventListener('keydown', this.handleKeyDown, this)
    document.addEventListener('touchmove', this.handleScroll, false)
    const { value, isMulti } = this.props

    let selectedValues = value

    if (isMulti && !Array.isArray(selectedValues)) {
      selectedValues = [selectedValues]
    }

    const options = this.getMappedOptions(this.props)
    const selectedOptions = this.findSelectedOptions(options, selectedValues)

    this.setState({
      value: selectedValues,
      options: options,
      selectedOptions: selectedOptions
    })
  }

  componentWillReceiveProps (nextProps) {
    if (!isEqual(nextProps.value, this.props.value) ||
      !isEqual(nextProps.children, this.props.children) ||
      !isEqual(nextProps.options, this.props.options)) {
      const options = this.getMappedOptions(nextProps)
      const selectedOptions = this.findSelectedOptions(options, nextProps.value)

      this.setState({
        value: nextProps.value,
        options: options,
        selectedOptions: selectedOptions
      })
    }
  }

  componentWillUnmount () {
    document.removeEventListener('mouseup', this.handleClickOutside, this)
    document.removeEventListener('keydown', this.handleKeyDown, this)
    document.removeEventListener('touchmove', this.handleScroll)
  }

  getSpacingClass () {
    if (this.props.spacing === undefined || this.props.spacing === null) return ''

    let spacing = typeof this.props.spacing === 'string' ? parseInt(this.props.spacing) : this.props.spacing
    if (spacing === 0) return ''
    if (spacing < 0) return ' pg-mb-special'
    return ' pg-mb-' + spacing
  }

  getStateStyle () {
    if (this.state.isOpen) {
      return ' open'
    } else {
      return ' closed'
    }
  }

  getStyle () {
    let classes = 'element-dropdown' + this.getSpacingClass() + this.getStateStyle()
    if (this.state.openOnTop) {
      classes += ' openOnTop'
    }
    if (this.props.className) {
      classes += ' ' + this.props.className
    }

    return classes
  }

  setRef (node) {
    this.dropDownRef = node
  }

  handleScroll (e) {
    if (this.dropDownRef && !this.dropDownRef.contains(e.target) && this.state.isOpen) {
      this.toggleOpen()
    }
  }

  handleClickOutside (e) {
    if (this.dropDownRef && !this.dropDownRef.contains(e.target) && this.state.isOpen) {
      this.toggleOpen()
    }
  }

  handleKeyDown (e) {
    // console.log('e', e, 'open', this.state.isOpen)
    if (!this.state.isOpen) return
    let options = this.getMappedOptions(this.props)
    let option = this.getClosestMatch(e.key, options)
    if (option !== null) {
      this.setState({ value: option.value, selectedOptions: option }, () => {
        // Scroll to selected item
        let listElement = this.dropDownRef.getElementsByClassName('drop__menu-list')[0].getElementsByClassName('drop__option--is-selected')[0]

        const isIExplorer = navigator.appCodeName.indexOf('Mozilla') !== -1 && navigator.appName.indexOf('Netscape') !== -1 && navigator.appVersion.indexOf('like Gecko') !== -1 && navigator.appVersion.indexOf('Apple') === -1 && navigator.appVersion.indexOf('Chrome') === -1
        const isEdge = navigator.userAgent.indexOf('Edge') > -1
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
        if (isIExplorer || isEdge || isSafari) {
          listElement.scrollIntoView(false)
        } else {
          listElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        }
      })
    }
  }

  /**
   * Gets the closest matching option for a given character (match against beginning character of each word in option labels.)
   * @param {String} char
   * @param {*} options
   */
  getClosestMatch (char, options) {
    char = char.toLowerCase()
    if (options.filter(x => Array.isArray(x.options) && x.options.length > 0)) {
      options = options.concat(options.filter(x => Array.isArray(x.options) && x.options.length > 0).flatMap(x => x.options))
    }
    let labels = options.map(o => o.label.toLowerCase())

    let maxLength = 0
    for (let i = 0; i < labels.length; i++) {
      let words = labels[i].split(' ')
      labels[i] = words.reduce((combined, word) => combined + word.substr(0, 1), '')
      if (labels[i].length > maxLength) {
        maxLength = labels[i].length
      }
    }

    for (let l = 0; l < maxLength; l++) {
      for (let i = 0; i < options.length; i++) {
        if (labels[i].substr(l, 1) === char) {
          return options[i]
        }
      }
    }
    return null
  }

  getMappedOptions (props) {
    const { children, options } = props
    // console.log('DropDown', { children, options })

    if (!children) {
      return options
    }

    const mapOption = ({ props: { children, ...props } }) => ({ label: children, ...props })
    const mapGroup = ({ props: { label, children, ...props } }) => {
      let groupOptions = []

      if (children) {
        // Map all the groups options to objects
        groupOptions = children.reduce(objectifyChild, [])
      }

      return {
        label: label,
        options: groupOptions,
        ...props
      }
    }

    const objectifyChild = (mapped, child) => {
      if (!child) {
        return mapped
      }

      // Is group
      if (child.type === 'optgroup') {
        return [...mapped, mapGroup(child)]
      }

      if (Array.isArray(child)) {
        // Map all array values to objects
        return [
          ...mapped,
          ...child.reduce(objectifyChild, [])
        ]
      }

      return [...mapped, mapOption(child)]
    }

    // Objectify all the children
    return children.reduce(objectifyChild, [])
  }

  onChange (e) {
    console.log('DropDown onChange triggered!', e)
    if (this.props.onChange !== null) {
      this.props.onChange(e)

      if (!this.props.isMulti) {
        this.toggleOpen()
      }
    }
  }

  toggleOpen () {
    const isOpen = !this.state.isOpen
    if (typeof this.props.onOpenChange === 'function') {
      this.props.onOpenChange(this.props.name, isOpen)
    }

    if (isOpen) {
      // Determine position of options after dom render
      setTimeout(() => {
        let openOnTop = this.dropDownRef && (window.innerHeight - this.dropDownRef.getBoundingClientRect().bottom < 300)
        let modalContent = document.getElementsByClassName('modal--content')[0]
        if (modalContent) {
          let modalContentBottom = modalContent.getBoundingClientRect().bottom
          let spaceAbove = this.dropDownRef.getBoundingClientRect().top - modalContent.getBoundingClientRect().top
          let spaceBelow = modalContent.getBoundingClientRect().bottom - this.dropDownRef.getBoundingClientRect().bottom

          // console.log('spaceAbove: ', spaceAbove, 'spaceBelow: ', spaceBelow, '(spaceBelow > spaceAbove) = ', spaceBelow > spaceAbove)
          openOnTop = openOnTop || modalContentBottom && (modalContentBottom - this.dropDownRef.getBoundingClientRect().bottom < 300)

          if (spaceBelow > spaceAbove) {
            openOnTop = false
          }
        }

        this.setState({
          openOnTop: openOnTop
        })
      }, 150)
    }

    this.setState({
      isOpen: isOpen,
      activatorStateClass: isOpen ? 'drop__activator--is-focused' : ''
    })
  }

  isSelected (option, value = null) {
    if (value === null) {
      value = this.state.value
    }
    if (value === null) return false

    if (this.props.isMulti) {
      return value.findIndex(opt => opt === option.value) > -1
    } else {
      // if (value === option.value) console.log('DROPDOWN - value, option.value ', value, option.value)
      return value === option.value
    }
  }

  findSelectedOptions (options, selectedValues = null) {
    if (!options) return []
    // console.log('DROPDOWN - selectedValues ', selectedValues)

    if (this.props.isGrouping) {
      // Filter groups to return selected values
      const selectedOptions = options.reduce((selected, group) => {
        let filtered = selected

        if (group.options) {
          filtered = [
            ...selected,
            ...group.options.filter(opt => this.isSelected(opt, selectedValues))
          ]
        } else {
          if (this.isSelected(group, selectedValues)) {
            filtered = [
              ...selected,
              group
            ]
          }
        }

        return filtered
      }, [])

      return selectedOptions
    } else if (this.props.isMulti) {
      return options.filter(opt => this.isSelected(opt, selectedValues))
    } else {
      return options.find(opt => this.isSelected(opt, selectedValues))
    }
  }

  render () {
    const { activatorStateClass, selectedOptions } = this.state
    let { name, options, disabled, children, labelText, helpText, isMulti, isGrouping, isSearchable } = this.props
    if (options === null && !children) return null

    // console.log('DropDown', { name, options, children, selectedOptions })

    const { isOpen } = this.state

    const renderDropDown = () => {
      const getLabel = () => {
        const getSelected = () => {
          if (!selectedOptions) return ''

          if ((isMulti || isGrouping) && Array.isArray(selectedOptions)) {
            return [...new Set(selectedOptions.map(opt => opt.label))].join(', ')
          }

          return selectedOptions.label
        }

        // Get selected label OR first of options
        const label = getSelected() || (this.state.options.length > 0 && this.state.options[0].label)
        if (!label && label !== '') return 'Select...'
        return label
      }
      let content = [
        <Activator
          classNamePrefix='drop'
          className={activatorStateClass}
          disabled={disabled}
          onClick={this.toggleOpen}
          labelText={getLabel()}
          size={this.props.size}
          style={{minWidth: this.props.minWidth}}
          key={0}
        />,
        <SelectBox
          name={name}
          value={selectedOptions}
          disabled={disabled}
          isMulti={isMulti}
          isSearchable={isSearchable}
          options={this.state.options}
          isSelected={this.isSelected}
          onChange={this.onChange}
          show={isOpen}
          openOnTop={this.state.openOnTop}
          key={1}
        />]

      return (
        <div ref={this.setRef} className={this.getStyle()}>
          {this.state.openOnTop ? content.reverse() : content}
        </div>
      )
    }

    const renderWrapper = (content) => {
      return (
        <LabelWrapper
          labelText={labelText}
          helpText={helpText}
        >
          {content}
        </LabelWrapper>
      )
    }

    return this.props.labelText !== null ? renderWrapper(renderDropDown()) : renderDropDown()
  }
}

DropDown.defaultProps = {
  size: 'l',
  style: 'default',
  labelText: null,
  helpText: null,
  onChange: null,
  value: null,
  options: null,
  disabled: false,
  isMulti: false,
  isGrouping: false,
  spacing: 0,
  minWidth: null
}

export default DropDown



// WEBPACK FOOTER //
// ./src/app/shared/elements/dropDown/index.js